/**
 * Shared joyride styles and a reusable per-page tour host.
 */
import { useCallback, useEffect, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import {
  hasCompletedPageTour,
  hasCompletedPortalTour,
  markPageTourCompleted,
} from "../tour/portalTour";
import { usePortalTour } from "./PortalTour";

export const joyrideStyles = {
  options: {
    zIndex: 12000,
    primaryColor: "#2563eb",
    textColor: "#111827",
    backgroundColor: "#ffffff",
    overlayColor: "rgba(15, 23, 42, 0.45)",
    arrowColor: "#ffffff",
  },
  tooltip: {
    borderRadius: 12,
    padding: 16,
    fontSize: 14,
  },
  tooltipTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 6,
  },
  tooltipContent: {
    padding: "4px 0 0",
    lineHeight: 1.45,
  },
  buttonNext: {
    backgroundColor: "#2563eb",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 14px",
  },
  buttonBack: {
    color: "#374151",
    fontSize: 13,
    marginRight: 8,
  },
  buttonSkip: {
    color: "#6b7280",
    fontSize: 13,
  },
};

/**
 * Compact control to start a page tour from the page header.
 */
export function TourThisPageButton({ onClick, disabled, label = "Tour this page" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="Walk through the controls on this page"
      style={{
        padding: "6px 12px",
        borderRadius: 8,
        border: "1px solid #bfdbfe",
        background: disabled ? "#e0f2fe" : "#eff6ff",
        color: "#1d4ed8",
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "default" : "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {disabled ? "Tour in progress…" : label}
    </button>
  );
}

/**
 * Per-page guided tour (auto-offer once + manual start).
 *
 * @param {{
 *   tourId: string,
 *   steps: object[],
 *   autoOffer?: boolean,
 *   ready?: boolean,
 * }} props
 */
export default function PageTour({
  tourId,
  steps,
  autoOffer = true,
  ready = true,
}) {
  const { userKey, tourRunning: portalTourRunning } = usePortalTour();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [autoOffered, setAutoOffered] = useState(false);

  const startTour = useCallback(() => {
    if (portalTourRunning) return;
    setStepIndex(0);
    setRun(true);
  }, [portalTourRunning]);

  useEffect(() => {
    if (!autoOffer || autoOffered || !ready || portalTourRunning) return;
    if (!userKey || !tourId) return;
    if (hasCompletedPageTour(userKey, tourId)) {
      setAutoOffered(true);
      return;
    }
    // Wait until the site-wide nav tour is done so two Joyrides don't stack.
    if (!hasCompletedPortalTour(userKey)) return;
    const timer = setTimeout(() => {
      setStepIndex(0);
      setRun(true);
      setAutoOffered(true);
    }, 650);
    return () => clearTimeout(timer);
  }, [autoOffer, autoOffered, ready, portalTourRunning, userKey, tourId]);

  useEffect(() => {
    if (portalTourRunning && run) setRun(false);
  }, [portalTourRunning, run]);

  const handleJoyride = useCallback(
    (data) => {
      const { status, action, type, index } = data;

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRun(false);
        setStepIndex(0);
        if (userKey && tourId) markPageTourCompleted(userKey, tourId);
      }
    },
    [userKey, tourId]
  );

  return (
    <>
      <TourThisPageButton
        onClick={startTour}
        disabled={run || portalTourRunning}
      />
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress
        disableOverlayClose
        scrollToFirstStep
        callback={handleJoyride}
        styles={joyrideStyles}
        locale={{
          back: "Back",
          close: "Close",
          last: "Done",
          next: "Next",
          skip: "Skip tour",
        }}
      />
    </>
  );
}
